<script setup lang="ts">
import { Mailing } from '@/@types/mailing'
import { PaginationData } from '@/@types/order'
import { Workshop } from '@/@types/workshop'
import { MailingsFilters, MailingsPagination, MailingsTable, WorkshopHeader, WorkshopsList } from '@/components/shared/admin/mailings'

import { Button } from '@/components/ui/button'
import { CardContent } from '@/components/ui/card'
import { Title } from '@/components/ui/title'
import LightLayout from '@/layouts/LightLayout.vue'
import { Head, Link, router, useForm, usePage } from '@inertiajs/vue3'
import { useDebounceFn } from '@vueuse/core'
import { computed, watch } from 'vue'

const props = defineProps<{
    workshop?: Workshop
    workshops?: Workshop[]
    mailings: Mailing[]
    pagination: PaginationData
    upgraded_mailings: String[]
}>()

const queryParams = new URLSearchParams(usePage().url.split('?')[1] || '')

const form = useForm({
    email_fullname: queryParams.get('email_fullname') || '',
    order_id: queryParams.get('order_id') || '',
    ticket_category: queryParams.get('ticket_category') || '',
    order_payment_type: queryParams.get('order_payment_type') || '',
    order_status: queryParams.get('order_status') || '',
    mailing_group: queryParams.get('mailing_group') || '',
    mailing_status: queryParams.get('mailing_status') || '',
    mailing_code: queryParams.get('mailing_code') || '',
})

const handleSubmit = useDebounceFn(() => {
    form.get(`/admin/mailings?workshop_id=${props.workshop?.id}`, {
        preserveScroll: true,
        preserveState: true,
    })
}, 300)

watch(
    () => form.data(),
    () => {
        handleSubmit()
    },
)

const currentPage = computed({
    get: () => props.pagination.current_page,
    set: (val) => {
        props.pagination.current_page = val
    },
})

watch(currentPage, () => {
    router.reload({
        data: {
            page: currentPage.value,
            workshop_id: props.workshop?.id,
            ...form.data(),
        },
    })
})
</script>

<template>
    <Head title="Gerenciar ingressos" />
    <div class="bg-slate-50">
        <LightLayout>
            <main class="container flex flex-col gap-8">
                <Title class="flex items-center gap-2 font-sans font-semibold"> Gerenciar ingressos </Title>

                <section class="flex flex-col gap-6" v-if="workshop">
                    <WorkshopHeader :workshop="workshop" :upgraded_mailings="upgraded_mailings">
                        <CardContent class="mt-10 lg:mt-20">
                            <MailingsFilters :form="form" />

                            <MailingsTable :mailings="props.mailings" />

                            <div v-if="!mailings.length" class="mt-8 flex flex-col items-center gap-6">
                                <p>Nada encontrado nos filtros selecionados</p>
                                <Button :as="Link" :href="`/admin/mailings?workshop_id=${workshop.id}`">
                                    {{ $t('Clear filters') }}
                                </Button>
                            </div>

                            <MailingsPagination
                                :pagination="props.pagination"
                                :workshop-id="workshop.id"
                                :form="form"
                                :current-page="currentPage"
                                @update:current-page="currentPage = $event"
                            />
                        </CardContent>
                    </WorkshopHeader>
                </section>

                <WorkshopsList v-else :workshops="workshops || []" />
            </main>
        </LightLayout>
    </div>
</template>
